import React, { useState, useEffect } from 'react';
import { PhoneCall, Clock, CheckCircle, XCircle, AlertCircle, RefreshCw, User, Calendar, ShieldCheck, MessageSquare, ArrowRight, FileText, CornerDownRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import apiClient from '@/utils/apiClient';
import { toast } from 'sonner';

const CallRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');
  
  // Modal states for Postpone / Reject reasons
  const [actionModal, setActionModal] = useState({
    isOpen: false,
    requestId: null,
    actionType: '', // 'reject' | 'postpone'
    reason: ''
  });

  const [submittingAction, setSubmittingAction] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [selectedStatus]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      let url = '/v1/warden/calls/';
      if (selectedStatus) url += `?status=${selectedStatus}`;
      const response = await apiClient.get(url);
      setRequests(response.data.requests || response.data || []);
    } catch (err) {
      toast.error('Failed to load parent requests');
    } finally {
      setLoading(false);
    }
  };

  const triggerAction = async (requestId, action, reason = '') => {
    setSubmittingAction(true);
    try {
      const payload = { action, reason };
      const response = await apiClient.post(`/v1/warden/calls/action/${requestId}`, payload);
      if (response.data.success) {
        toast.success(`Request ${action}ed successfully`);
        setActionModal({ isOpen: false, requestId: null, actionType: '', reason: '' });
        fetchRequests();
      } else {
        toast.error(response.data.message || 'Action failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update request');
    } finally {
      setSubmittingAction(false);
    }
  };

  const handleOpenActionModal = (requestId, actionType) => {
    setActionModal({
      isOpen: true,
      requestId,
      actionType,
      reason: ''
    });
  };

  const handleConfirmModalAction = (e) => {
    e.preventDefault();
    if (!actionModal.reason.trim()) {
      toast.error(`Please provide a reason to ${actionModal.actionType} this request.`);
      return;
    }
    triggerAction(actionModal.requestId, actionModal.actionType, actionModal.reason);
  };

  const getStatusBadge = (req) => {
    const status = req.status;
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-500 text-white gap-1"><Clock className="w-3 h-3" /> Pending</Badge>;
      case 'accepted':
      case 'approved':
        return <Badge className="bg-emerald-600 text-white gap-1"><CheckCircle className="w-3 h-3" /> Accepted</Badge>;
      case 'postponed':
        return <Badge className="bg-blue-600 text-white gap-1"><Clock className="w-3 h-3" /> Postponed</Badge>;
      case 'rejected':
        return <Badge className="bg-rose-600 text-white gap-1"><XCircle className="w-3 h-3" /> Rejected</Badge>;
      case 'done':
      case 'completed':
        return <Badge className="bg-slate-700 text-white gap-1"><CheckCircle className="w-3 h-3" /> Done</Badge>;
      case 'cancelled':
        return <Badge className="bg-slate-400 text-white gap-1"><XCircle className="w-3 h-3" /> Cancelled by Parent</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6 animate-fade-in" data-testid="call-requests">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-3">
            <PhoneCall className="w-8 h-8 text-blue-600" />
            Parent Requests Management
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Review parent call-back requests, leave permissions, and special inquiries for assigned students
          </p>
        </div>
        <Button onClick={fetchRequests} variant="outline" size="sm" className="gap-1.5">
          <RefreshCw className="w-4 h-4" /> Refresh List
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap items-center gap-2 bg-slate-100/80 p-1.5 rounded-xl text-sm font-medium">
        <button
          onClick={() => setSelectedStatus('')}
          className={`px-4 py-2 rounded-lg transition-all ${selectedStatus === '' ? 'bg-white text-slate-800 shadow-sm font-bold' : 'text-slate-600 hover:text-slate-900'}`}
        >
          All Requests ({requests.length})
        </button>
        <button
          onClick={() => setSelectedStatus('pending')}
          className={`px-4 py-2 rounded-lg transition-all ${selectedStatus === 'pending' ? 'bg-white text-amber-700 shadow-sm font-bold' : 'text-slate-600 hover:text-slate-900'}`}
        >
          Pending ({requests.filter(r => r.status === 'pending').length})
        </button>
        <button
          onClick={() => setSelectedStatus('accepted')}
          className={`px-4 py-2 rounded-lg transition-all ${selectedStatus === 'accepted' ? 'bg-white text-emerald-700 shadow-sm font-bold' : 'text-slate-600 hover:text-slate-900'}`}
        >
          Accepted
        </button>
        <button
          onClick={() => setSelectedStatus('postponed')}
          className={`px-4 py-2 rounded-lg transition-all ${selectedStatus === 'postponed' ? 'bg-white text-blue-700 shadow-sm font-bold' : 'text-slate-600 hover:text-slate-900'}`}
        >
          Postponed
        </button>
        <button
          onClick={() => setSelectedStatus('rejected')}
          className={`px-4 py-2 rounded-lg transition-all ${selectedStatus === 'rejected' ? 'bg-white text-rose-700 shadow-sm font-bold' : 'text-slate-600 hover:text-slate-900'}`}
        >
          Rejected
        </button>
        <button
          onClick={() => setSelectedStatus('done')}
          className={`px-4 py-2 rounded-lg transition-all ${selectedStatus === 'done' ? 'bg-white text-slate-800 shadow-sm font-bold' : 'text-slate-600 hover:text-slate-900'}`}
        >
          Done
        </button>
      </div>

      {/* Requests Feed */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading parent requests...</div>
        ) : requests.length === 0 ? (
          <Card className="p-8 text-center border-dashed">
            <CheckCircle className="w-12 h-12 mx-auto text-emerald-400 mb-2" />
            <p className="text-slate-700 font-semibold">No requests found</p>
            <p className="text-slate-400 text-xs mt-1">There are no parent requests in this view.</p>
          </Card>
        ) : (
          requests.map((req) => (
            <Card key={req.id} className="border border-slate-200 hover:shadow-md transition-all overflow-hidden">
              <CardContent className="p-6 space-y-4">
                {/* Header Badge Row */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-600 text-white">{req.request_type || 'Call Request'}</Badge>
                    {req.priority && (
                      <Badge variant={req.priority === 'Urgent' ? 'destructive' : 'outline'}>
                        {req.priority} Priority
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {getStatusBadge(req)}
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(req.created_at || Date.now()).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Special Case: Admin Action Notice */}
                {(req.updated_by_role === 'admin' || req.admin_note) && (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-xs text-purple-900 flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-bold">🛡️ Admin Action Taken:</span> Status was modified by Admin ({req.updated_by_name || 'Administrator'}) to <strong className="uppercase">{req.status}</strong>.
                      {req.admin_note && (
                        <p className="mt-1 text-purple-800 italic">"Admin Note: {req.admin_note}"</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Request Description */}
                <div>
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Request Description</h4>
                  <p className="text-slate-800 text-sm font-medium leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-100">
                    {req.reason}
                  </p>
                </div>

                {/* Additional Details: What, When, Who */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-slate-50/70 p-4 rounded-xl border border-slate-200 text-xs">
                  <div>
                    <span className="font-bold text-slate-700 flex items-center gap-1 mb-1">
                      🎯 What to Happen:
                    </span>
                    <p className="text-slate-800 font-medium">{req.what_to_happen || 'Call-back'}</p>
                  </div>

                  <div>
                    <span className="font-bold text-slate-700 flex items-center gap-1 mb-1">
                      📅 When:
                    </span>
                    <p className="text-slate-800 font-medium">{req.when_date || req.preferred_time || 'As soon as possible'}</p>
                  </div>

                  <div>
                    <span className="font-bold text-slate-700 flex items-center gap-1 mb-1">
                      👤 Who (Child & Parent):
                    </span>
                    <p className="text-slate-800 font-semibold">Child: {req.student_name} ({req.class_name || 'Class N/A'})</p>
                    <p className="text-slate-600">Parent: {req.parent_name} ({req.parent_phone})</p>
                  </div>
                </div>

                {/* Reason Notes Display if Postponed / Rejected */}
                {(req.status === 'rejected' || req.rejection_reason) && (
                  <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs text-rose-900 flex items-start gap-2">
                    <CornerDownRight className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                    <div>
                      <span><strong>Rejection Reason:</strong> {req.rejection_reason || (req.warden_note ? req.warden_note.replace(/^Rejected:\s*/i, '') : req.admin_note || 'No reason provided')}</span>
                      {req.updated_by_name && (
                        <p className="text-[11px] text-rose-500 mt-0.5">Rejected by: {req.updated_by_name} ({req.updated_by_role || 'Warden'})</p>
                      )}
                    </div>
                  </div>
                )}

                {(req.status === 'postponed' || req.postponed_reason) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900 flex items-start gap-2">
                    <CornerDownRight className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <span><strong>Postponed Note / Reschedule:</strong> {req.postponed_reason || (req.warden_note ? req.warden_note.replace(/^Postponed:\s*/i, '') : req.admin_note)}</span>
                      {req.updated_by_name && (
                        <p className="text-[11px] text-blue-500 mt-0.5">Updated by: {req.updated_by_name} ({req.updated_by_role || 'Warden'})</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Warden Action Buttons */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-100">
                  <a
                    href={`tel:${req.parent_phone}`}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                  >
                    <PhoneCall className="w-3.5 h-3.5 text-blue-600" /> Call Parent ({req.parent_phone})
                  </a>

                  <div className="flex flex-wrap items-center gap-2">
                    {/* Action: Accept */}
                    {req.status !== 'accepted' && req.status !== 'done' && (
                      <Button
                        onClick={() => triggerAction(req.id, 'accept')}
                        disabled={submittingAction}
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1"
                      >
                        <CheckCircle className="w-3.5 h-3.5" /> Accept
                      </Button>
                    )}

                    {/* Action: Postpone (with reason) */}
                    {req.status !== 'done' && req.status !== 'cancelled' && (
                      <Button
                        onClick={() => handleOpenActionModal(req.id, 'postpone')}
                        disabled={submittingAction}
                        variant="outline"
                        size="sm"
                        className="border-blue-300 text-blue-700 hover:bg-blue-50 text-xs gap-1"
                      >
                        <Clock className="w-3.5 h-3.5 text-blue-600" /> Postpone...
                      </Button>
                    )}

                    {/* Action: Reject (with reason) */}
                    {req.status !== 'rejected' && req.status !== 'done' && req.status !== 'cancelled' && (
                      <Button
                        onClick={() => handleOpenActionModal(req.id, 'reject')}
                        disabled={submittingAction}
                        variant="outline"
                        size="sm"
                        className="border-rose-300 text-rose-700 hover:bg-rose-50 text-xs gap-1"
                      >
                        <XCircle className="w-3.5 h-3.5 text-rose-600" /> Reject...
                      </Button>
                    )}

                    {/* Action: Done */}
                    {req.status !== 'done' && (
                      <Button
                        onClick={() => triggerAction(req.id, 'done')}
                        disabled={submittingAction}
                        size="sm"
                        className="bg-slate-800 hover:bg-slate-900 text-white text-xs gap-1"
                      >
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> Mark Done
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Reason Dialog Modal for Reject and Postpone */}
      <Dialog open={actionModal.isOpen} onOpenChange={(open) => !open && setActionModal(prev => ({ ...prev, isOpen: false }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
              {actionModal.actionType === 'reject' ? (
                <>
                  <XCircle className="w-5 h-5 text-rose-600" />
                  Reason for Rejecting Request
                </>
              ) : (
                <>
                  <Clock className="w-5 h-5 text-blue-600" />
                  Reason / Reschedule Time for Postponing
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleConfirmModalAction} className="space-y-4 pt-2">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">
                {actionModal.actionType === 'reject' ? 'Rejection Reason *' : 'Postponed Note / New Time *'}
              </label>
              <textarea
                rows={3}
                required
                placeholder={
                  actionModal.actionType === 'reject'
                    ? 'Explain why this request is being rejected (e.g. Warden unavailable during class hours, Outpass rules)...'
                    : 'Provide postponed note or new proposed time (e.g. Rescheduled to Sunday 7:00 PM due to evening study hour)...'
                }
                className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500"
                value={actionModal.reason}
                onChange={(e) => setActionModal({ ...actionModal, reason: e.target.value })}
              />
            </div>

            <DialogFooter className="gap-2 pt-2 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setActionModal(prev => ({ ...prev, isOpen: false }))}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submittingAction}
                className={actionModal.actionType === 'reject' ? 'bg-rose-600 hover:bg-rose-700 text-white font-bold' : 'bg-blue-600 hover:bg-blue-700 text-white font-bold'}
              >
                {submittingAction ? 'Updating...' : `Confirm ${actionModal.actionType === 'reject' ? 'Rejection' : 'Postpone'}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CallRequests;
